import React, { useState } from 'react';
import { modulesAPI } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DISCLAIMER_LEGAL = `⚠️ AVISO IMPORTANTE:
Esta información es de carácter educativo e informativo únicamente.
No constituye asesoría legal profesional ni opinión jurídica vinculante.
No reemplaza la consulta con un abogado titulado.`;

const Legal = () => {
  const [requestType, setRequestType] = useState('legal_info');
  const [query, setQuery] = useState('');
  const [documentText, setDocumentText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const requestTypes = [
    { 
      id: 'legal_info', 
      name: '📚 Información Legal',
      description: 'Explicación de leyes en lenguaje claro'
    },
    { 
      id: 'legal_orientation', 
      name: '🧭 Orientación General',
      description: 'Ejemplos y buenas prácticas'
    },
    { 
      id: 'analyze_contract', 
      name: '📄 Revisión de Contrato',
      description: 'Análisis informativo (no vinculante)'
    },
    { 
      id: 'verify_law', 
      name: '✅ Verificar Normativa',
      description: 'Confirmar existencia de leyes'
    }
  ];

  const handleProcess = async () => {
    setLoading(true);
    setResult(null);

    try {
      const response = await modulesAPI.legalProcess({ 
        request_type: requestType, 
        query: query,
        document_text: documentText
      });
      setResult(response.data);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      setResult({ 
        success: false, 
        error: typeof errorDetail === 'object' ? errorDetail.message : errorDetail || 'Error al procesar solicitud',
        disclaimer: DISCLAIMER_LEGAL
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
      <div className="px-4 sm:px-10 md:px-20 lg:px-40">
        <Header />
        
        <div className="py-8 max-w-4xl mx-auto">
          {/* Header con nuevo nombre */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              ⚖️ Asistente de Información Legal
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Información legal general y verificación documental con fines educativos.
            </p>
          </div>

          {/* Disclaimer permanente */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="font-bold text-amber-800 dark:text-amber-300 mb-1">
                  Aviso Importante
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Esta información es <strong>educativa e informativa</strong>. 
                  No constituye asesoría legal y no reemplaza la consulta con un abogado titulado.
                </p>
              </div>
            </div>
          </div>

          {/* Selector de tipo */}
          <div className="bg-white dark:bg-[#1a2530] rounded-xl p-6 shadow-sm mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Tipo de Consulta
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {requestTypes.map(type => (
                <button
                  key={type.id}
                  onClick={() => setRequestType(type.id)}
                  className={`p-4 rounded-lg text-left transition-all ${
                    requestType === type.id
                      ? 'bg-indigo-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <div className="font-medium">{type.name}</div>
                  <div className={`text-xs mt-1 ${requestType === type.id ? 'text-indigo-100' : 'text-gray-500'}`}>
                    {type.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Formulario */}
          <div className="bg-white dark:bg-[#1a2530] rounded-xl p-6 shadow-sm mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {requestType === 'analyze_contract' ? 'Documento a Revisar' : 'Tu Consulta'}
            </h2>
            
            {requestType === 'analyze_contract' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Texto del Contrato
                  </label>
                  <textarea
                    value={documentText}
                    onChange={(e) => setDocumentText(e.target.value)}
                    placeholder="Pega aquí el texto del contrato que deseas revisar..."
                    rows={10}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                  />
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-blue-700 dark:text-blue-300 text-sm">
                  💡 <strong>Nota:</strong> Esta revisión es informativa y no vinculante. 
                  Antes de firmar cualquier contrato, consulte con un abogado.
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {requestType === 'verify_law' ? '¿Qué ley o normativa deseas verificar?' : '¿Cuál es tu consulta?'}
                  </label>
                  <textarea
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={
                      requestType === 'legal_info' 
                        ? "Ej: ¿Cuáles son los derechos del consumidor en Chile?"
                        : requestType === 'verify_law'
                        ? "Ej: ¿Existe la Ley 19.496 de Protección al Consumidor?"
                        : "Describe tu situación de forma general..."
                    }
                    rows={4}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            )}

            <button
              onClick={handleProcess}
              disabled={loading || (!query && !documentText)}
              className="mt-6 w-full py-3 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? '⏳ Procesando...' : '📋 Obtener Información'}
            </button>
          </div>

          {/* Resultado */}
          {result && (
            <div className="bg-white dark:bg-[#1a2530] rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {result.success ? '📋 Información' : '❌ Error'}
                </h2>
                {result.result_type && (
                  <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-bold rounded-full">
                    {result.result_type}
                  </span>
                )}
              </div>

              {result.success ? (
                <div className="space-y-4">
                  {/* Contenido */}
                  <div className="prose dark:prose-invert max-w-none">
                    <pre className="whitespace-pre-wrap text-sm bg-gray-50 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto">
                      {result.information || result.review || result.verification || result.orientation}
                    </pre>
                  </div>

                  {/* Warning adicional */}
                  {result.warning && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg text-orange-700 dark:text-orange-300 text-sm">
                      {result.warning}
                    </div>
                  )}

                  {/* Recomendación */}
                  {result.recommendation && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-blue-700 dark:text-blue-300 text-sm">
                      💡 <strong>Recomendación:</strong> {result.recommendation}
                    </div>
                  )}

                  {/* Nota */}
                  {result.note && (
                    <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-gray-600 dark:text-gray-400 text-sm">
                      📌 {result.note}
                    </div>
                  )}

                  {/* Disclaimer del resultado */}
                  <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-line">
                      {result.disclaimer}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-red-600 dark:text-red-400">
                  <p>{result.error}</p>
                  {result.disclaimer && (
                    <p className="mt-4 text-xs text-gray-600 dark:text-gray-400 whitespace-pre-line">
                      {result.disclaimer}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Info de limitaciones */}
          <div className="mt-6 bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
            <h3 className="font-bold text-gray-800 dark:text-gray-200 mb-3">
              ℹ️ Sobre este Asistente
            </h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-medium text-green-600 dark:text-green-400 mb-2">✅ Qué SÍ puedes hacer:</h4>
                <ul className="text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• Obtener información legal general</li>
                  <li>• Revisar contratos (informativo)</li>
                  <li>• Verificar si existe una ley</li>
                  <li>• Conocer riesgos comunes</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-red-600 dark:text-red-400 mb-2">❌ Qué NO hace:</h4>
                <ul className="text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• Asesoría legal personalizada</li>
                  <li>• Redactar demandas</li>
                  <li>• Representación legal</li>
                  <li>• Garantizar resultados</li>
                </ul>
              </div>
            </div>
            <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
              <p className="text-sm text-indigo-700 dark:text-indigo-300">
                <strong>Para decisiones legales importantes:</strong> Consulte siempre con un abogado titulado 
                que pueda evaluar su caso específico.
              </p>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default Legal;
